import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row } from '@tanstack/react-table';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Transaction from '@/models/Transaction';
import { isFutureRow } from './futureTransactions';
import { balanceNotComputedBefore } from './runningBalance';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import { InquiryResourceField } from '@/models/common/InquiryResourceField';
import Sheet, { SheetItem } from '@/components/ui/Sheet/Sheet';
import { formatDateShort } from '@/helpers/dateHelper';
import { formatCurrencyShort } from '@/helpers/currencyHelper';
import {
  CardAction,
  CardAmount,
  CardBody,
  CardIcon,
  CardIconButton,
  CardMeta,
  CardRowActions,
  CardSelect,
  CardTitle,
  ListCard,
} from '@/components/ui/CardList/CardList';
import donation from '@/assets/icons/donation.svg';
import payment from '@/assets/icons/payment.svg';
import support from '@/assets/icons/cell-support.svg';

/*
 * ============================================================
 *  כרטיס תנועה — תצוגת המסך הצר של לוח התנועות
 * ============================================================
 *
 * המבנה הוא של השורה השטוחה מ-hatzdaka-nedarim
 * (dashboard.js, TAB_CONFIG['transactions'].card): עיגול אייקון,
 * כותרת "תרומה מאת רמי לוי", שורת מטא, וסכום בקצה.
 *
 * ⚠️ **מה שהכרטיס הזה מחזיר, ולא רק מעצב.** בטבלה חמש מהעמודות
 * מסומנות hiddenOnMobile, כלומר במסך צר הן פשוט לא היו מוצגות:
 * הבחירה, סוג התנועה, אמצעי התשלום, הקבלה ושלוש פעולות השורה.
 * כאן כולן חוזרות — השדות לשורת המטא, והפעולות לגיליון התחתון.
 * זה הכלל שהמסך הזה נבנה סביבו: המעבר לנייד משנה **איך** דבר
 * מוצג, אף פעם לא **אם**.
 *
 * ההבדל היחיד מנדרים בכיוון הזה הוא במכוון: שם לשורת התנועה יש
 * פעולה אחת (פתיחת פנייה), כי ה-WebView שלהם לא יודע להוריד קובץ
 * ולא להציג PDF. באתר אין את המגבלה, ולכן ההורדה, השליחה במייל
 * וצפייה בקבלה נשארות — בתבנית שנדרים עצמו משתמש בה כשיש יותר
 * מפעולה אחת: תפריט קיבוב שפותח גיליון (RowMenu.build שם).
 *
 * ⚠️ **שורת המטא מוגבלת לשניים-שלושה שדות, לא לחמישה.** מספר הקבלה
 * ושורת ההמרה היו כאן בעבר כטקסט, כדי שמידע שבדסקטופ יושב רק
 * ב-tooltip (שאין לו מקבילה במסך מגע) לא ייעלם. הם עברו למגירת
 * הפרטים — הקבלה כבר הייתה שם עם כפתורי הורדה ושליחה, וההמרה
 * קיבלה שם סעיף חדש (AdditionalInfo.tsx, conversionSection). שום
 * מידע לא אבד; הוא רק לא צריך להיכתב פעמיים על אותו כרטיס, בשביל
 * שהוא ייראה כמו שאר שני הלוחות שבסרגל הטאבים ולא "מלא".
 */

/** הסוגים שיש להם קבלה. זהה לרשימה ב-TransactionRowActions ובתא הקבלה. */
const KINDS_WITH_RECEIPT = ['תרומה', 'פיקדון', 'הלוואה'];

interface TransactionCardProps {
  row: Row<Transaction>;
  isActive: boolean;
  onOpen: (transaction: Transaction) => void;
  onDownloadReceipt: (transaction: Transaction) => void;
  onSendReceiptEmail: (transaction: Transaction) => void;
  onViewReceipt: (transaction: Transaction) => void;
  /** תיבת הסימון מוצגת רק במצב בחירה מרובה (ר' DataTable). */
  selectionMode?: boolean;
  /** HAT-1436: המטבע של הקרן, לעיצוב יתרת השורה — כמו שכבר מקבל SelectedTransactionsSummary. */
  fundCurrency?: string;
  /** HAT-1448: the first date with a running balance, so an older row says «לא מחושבת לפני …». */
  runningBalanceFrom?: string | null;
}

const TransactionCard = ({
  row,
  isActive,
  onOpen,
  onDownloadReceipt,
  onSendReceiptEmail,
  onViewReceipt,
  selectionMode = false,
  fundCurrency,
  runningBalanceFrom,
}: TransactionCardProps) => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const transaction = row.original;

  /*
   * ⚠️ אותו מיפוי בדיוק כמו ב-ColumnsHelpers (MultiText ו-
   * TransactionType). הוא נכתב כאן שוב ולא חולץ לשיתוף, כי חילוץ
   * היה משנה קובץ שכל הלוחות בפרויקט עוברים דרכו — ובמשימה שכל
   * כולה עיצוב, שינוי כזה הוא בדיוק הסוג של דבר שמפיל התנהגות
   * במקום אחר. אם המיפוי ישתנה אי־פעם, שני המקומות האלה הם מה
   * שצריך לעדכן, והתגובה הזאת היא הסימן.
   */
  const kindPrefix = (): string => {
    switch (transaction.kind) {
      case 'תרומה':
        return t('donationFrom');
      case 'תשלום ספקים':
      case 'תשלום ספקים (הוראת קבע)':
        return t('paymentTo');
      case 'תמיכה':
      case 'מלגה':
        return t('supportFor');
      default:
        return '';
    }
  };

  const kindLabel = (): string => {
    switch (transaction.kind) {
      case 'תרומה':
        return t('donationType');
      case 'תשלום ספקים':
      case 'תשלום ספקים (הוראת קבע)':
        return t('paymentType');
      case 'תמיכה':
      case 'מלגה':
        return t('supportType');
      default:
        return transaction.kind;
    }
  };

  /*
   * ⚠️ תמיד מחזירה אייקון, אף פעם לא undefined — כמו transactionIconClass
   * במקור. שם ה-switch נופל ל-icon-payout כברירת מחדל לכל סוג שאינו
   * תרומה/תמיכה (כולל תשלום לספקים, שמקבל שם בדיוק את אותו אייקון
   * "תשלום יוצא" כללי). כאן `payment` ממלא את אותו תפקיד: לא רק
   * לתשלום ספקים, אלא לכל סוג שלא זוהה. עיגול ריק — שהיה קורה כאן
   * קודם לכל סוג לא-ידוע — הוא בדיוק מה שהמקור נמנע ממנו.
   */
  const kindIcon = (): string => {
    switch (transaction.kind) {
      case 'תרומה':
        return donation;
      case 'תמיכה':
      case 'מלגה':
        return support;
      default:
        return payment;
    }
  };

  const formatAmount = (value: number, currency?: string) =>
    value.toLocaleString(i18n.language, { style: 'currency', currency: currency || 'ILS' });

  /*
   * HAT-1436: the running balance after this row, in the meta line rather than as a
   * second CardAmount - the card's own rule is that mobile changes how a field is
   * shown, never whether (see the file header). Same "no real balance" rows as the
   * desktop column (TransactionPage's cumulativeAmount column) get no line at all,
   * rather than a balance of zero nobody actually had.
   */
  const balanceLabel = (): string | null => {
    const notComputedBefore = balanceNotComputedBefore(transaction, runningBalanceFrom);
    if (notComputedBefore) return t('cumulativeAmountCardNotComputedBefore', { date: formatDateShort(notComputedBefore) });
    if (isFutureRow(transaction) || !transaction.cumulativeAmount) return null;
    return t('cumulativeAmountCardLabel', { amount: formatAmount(transaction.cumulativeAmount, fundCurrency || transaction.currency) });
  };

  const prefix = kindPrefix();
  const isReceiptRelevant = KINDS_WITH_RECEIPT.includes(transaction.kind);
  const hasReceipt = isReceiptRelevant && Boolean(transaction.receiptId);

  /* מה שהפנייה נושאת איתה — זהה ל-TransactionRowActions, כדי שפנייה
     שנפתחת מהנייד תגיע למשרד עם אותם פרטים כמו מהדסקטופ. */
  const resourceSummary: InquiryResourceField[] = [
    { label: t('date'), value: new Date(transaction.date).toLocaleDateString('he-IL') },
    { label: t('amount'), value: formatAmount(transaction.amount, transaction.currency) },
    { label: t('kind'), value: transaction.kind },
    { label: t('paymentMethod'), value: transaction.paymentMethod ? t(transaction.paymentMethod) : '' },
    { label: t('contactName'), value: transaction.contactName },
    { label: t('inquiries.resource.reference'), value: transaction.donationReference ?? '' },
  ].filter((field) => !!field.value);

  const menuItems: SheetItem[] = [];
  if (isReceiptRelevant) {
    menuItems.push({
      key: 'view',
      label: t('receipt'),
      disabled: !hasReceipt,
      onClick: () => onViewReceipt(transaction),
    });
    menuItems.push({
      key: 'download',
      label: t('downloadReceipt'),
      disabled: !hasReceipt,
      onClick: () => onDownloadReceipt(transaction),
    });
    menuItems.push({
      key: 'email',
      label: t('sendReceiptByEmail'),
      disabled: !hasReceipt,
      onClick: () => onSendReceiptEmail(transaction),
    });
  }


  return (
    <>
      <ListCard isActive={isActive} onClick={() => onOpen(transaction)}>
        {/* הבחירה המרובה מזינה את "שליחת קבלות" ואת "ייצוא פרטי תנועות".
            שתיהן פעולות קיימות, ולכן היא נשארת — אבל נפתחת מכפתור,
            ולא יושבת על כל כרטיס דרך קבע (ר' DataTable). */}
        {selectionMode && (
          <CardSelect>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} />
          </CardSelect>
        )}

        <CardIcon>
          <img src={kindIcon()} alt="" />
        </CardIcon>

        <CardBody>
          <CardTitle strong>{prefix ? `${prefix} ${transaction.contactName || ''}` : transaction.contactName || ''}</CardTitle>
          <CardMeta
            items={[
              formatDateShort(transaction.date),
              prefix ? null : kindLabel(),
              transaction.paymentMethod ? t(transaction.paymentMethod) : null,
              balanceLabel(),
            ]}
          />
        </CardBody>

        <CardAmount positive={transaction.amount >= 0}>
          {formatCurrencyShort(transaction.amount, transaction.currency)}
        </CardAmount>

        <CardRowActions>
          {/* פתיחת פנייה נשארת בשורה עצמה, כמו במקור. הדגל מחזיר null
              כשהוא כבוי, ואז נשאר רק תפריט הקיבוב. */}
          {/* סדר קבוע, צמוד לקצה השמאלי: ⋮ (כשיש) ואז הפנייה, שהיא תמיד
              האייקון הקיצוני. בלי מקום שמור — שורה בלי תפריט פשוט קצרה יותר. */}
          {menuItems.length > 0 && (
            <CardIconButton title={t('actions')} onClick={() => setIsMenuOpen(true)}>
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </CardIconButton>
          )}
          <CardAction>
            <OpenInquiryButton
              resourceType="Transaction"
              resourceId={String(transaction.transactionId)}
              resourceSummary={resourceSummary}
              size="small"
            />
          </CardAction>
        </CardRowActions>
      </ListCard>

      <Sheet
        open={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        title={prefix ? `${prefix} ${transaction.contactName || ''}` : transaction.contactName || ''}
        items={menuItems}
      />
    </>
  );
};

export default TransactionCard;
